import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import styles from './Components/CSS/BlogSwipper.module.css';
import { useBlogs } from '../hooks/useBlogs';
import { FreeMode, Mousewheel } from 'swiper/modules';

const BlogSwipper = () => {
  const navigate = useNavigate();
  const { blogs, isLoading, error, refetchBlogs } = useBlogs();

  // Add this useEffect to handle initial data fetching
  useEffect(() => {
    refetchBlogs();
  }, [refetchBlogs]);

  const stripHtmlTags = (html) => {
    return (html || '').replace(/<[^>]*>/g, '');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>Error loading blogs</div>;
  }

  if (!blogs?.blogs || blogs.blogs.length === 0) {
    return (
      <div className={styles.noBlogsContainer}>
        <p className={styles.noBlogs}>No blogs available at the moment</p>
      </div>
    );
  }

  const blogPosts = blogs.blogs;

  return (
    <>
    <div className={styles.sectionHeader}>
        <h2>Blogs</h2>
        </div>
    <Swiper
      modules={[Mousewheel, FreeMode]}
      freeMode={{
        enabled: true,
        momentum: true,
        momentumRatio: 1, // smoother momentum
        momentumVelocityRatio: 1, // normal velocity handling
        minimumVelocity: 0.02, // keep this small to allow tiny moves
      }}
      speed={400} // Reduce speed slightly for softer acceleration
      resistance={true} // Allow a bit of resistance (helps feel natural)
      resistanceRatio={0.85}
      mousewheel={{ forceToAxis: true, invert: false, sensitivity: 1 }}
      spaceBetween={20}
      slidesPerView="auto"
      className={styles.swiper}
      breakpoints={{
        320: { slidesPerView: 2, spaceBetween: 8 },
        480: { slidesPerView: 2.5, spaceBetween: 10 },
        768: { slidesPerView: 3, spaceBetween: 10 },
        1024: { slidesPerView: 4, spaceBetween: 10 },
        1440: { slidesPerView: 4, spaceBetween: 10 },
      }}
    >
      {blogPosts.map((blog) => {
        const plainTextContent = stripHtmlTags(blog.content);
        const preview = plainTextContent
          .split(/\s+/)
          .slice(0, 15)
          .join(" ") + "...";

        return (
          <SwiperSlide key={blog.id} className={styles.swiperSlide}>
            <div 
              className={styles.card} 
              onClick={() => navigate(`/blog/${blog.slug}`)}
            >
              <div className={styles.imageContainer}>
                <a href="#" className={styles.category}>
                  {blog.category || 'Uncategorized'}
                </a>
                {blog.featuredImage ? (
                  <img
                    src={blog.featuredImage}
                    alt={blog.title}
                    className={styles.image}
                  />
                ) : (
                  <div className={`${styles.image} ${styles.placeholderImage}`}>
                    No image available
                  </div>
                )}
              </div>

              <div className={styles.content}>
                <h3 className={styles.title}>
                  {blog.title.length > 40 ? `${blog.title.slice(0, 40)}...` : blog.title}
                </h3>
                <div className={styles.divider}></div>

                <div className={styles.authorSection}>
                  <div className={styles.author}>
                    <div className={styles.avatarPlaceholder}>
                      {blog.author?.name?.charAt(0) || '?'}
                    </div>
                    <span>{blog.author?.name || 'Unknown Author'}</span>
                  </div>
                  <span className={styles.date}>
                    {formatDate(blog.createdAt)}
                  </span>
                </div>

                <p className={styles.description}>{preview}</p>
              </div>
            </div>
          </SwiperSlide>
        );
      })}
    </Swiper>
    </>
  );
};

export default BlogSwipper;